/**
 * Rolling per-tack means for the live-map calibration overlay.
 *
 * Two families of view, both averaging the same rows over the same window and both split PORT vs
 * STBD within a point-of-sail band:
 *
 * **Sensor comparison** ({@link computeTwaTackStats}) — bow vane against masthead unit, one metric at
 * a time. A vane offset shows up as a large PORT/STBD delta on one sensor while the other stays near
 * zero.
 * - `twa` — mean |TWA| (`Twa_bow_deg` / `Twa_mhu_deg`), abs so both tacks are directly comparable,
 *   DELTA = STBD - PORT (positive = starboard reads wider).
 * - `twd` — mean TWD (`Twd_bow_deg` / `Twd_mhu_deg`). A compass bearing, so the mean is *circular*
 *   (vector mean, never abs — 350° and 10° average to 0°, not 180°) and DELTA is the wrapped
 *   difference in -180..180.
 *
 * **Single-sensor readout** ({@link computeTwaTackSensorStats}) — TWS, BSP and TWA for one sensor, so
 * the crew can see what that sensor reads and how the boat performs against it on each tack. BSP is a
 * boat channel, not a per-sensor one, so its row is identical in both sensor views by design: it is
 * the reference the sensor readings are judged against.
 *
 * Which point-of-sail band a sample belongs to, and which tack it is on, always come from the
 * canonical `Twa_deg` / `TACK` regardless of view — only the averaged values change.
 */
import { normalizeAngle, normalizeAngleDiff } from '../components/charts/map/overlays/mapUtils';
import { speedScaleToKph } from './boatWindSamples';
import { readNumericFromPoint, type FocusPosMode } from './liveTableFocusFilters';
import {
  convertSpeedMagnitude,
  SPEED_UNIT_KPH,
  SPEED_UNIT_KTS,
  type SpeedDisplayUnit,
} from './speedUnits';

export type TwaTackSensor = 'mhu' | 'bow';

/** Which wind quantity the overlay is averaging. */
export type TwaTackMetric = 'twa' | 'twd';

/** Sensor rows shown in the overlay, in display order. */
export const TWA_TACK_SENSORS: readonly TwaTackSensor[] = ['mhu', 'bow'];

/**
 * Candidate channels per sensor, best first. Deployments differ on which TWA solution is streamed —
 * the raw SGP vane, the team-model solution, or only the tack-normalised (abs) variant.
 */
export const TWA_TACK_SENSOR_CHANNELS: Record<TwaTackSensor, readonly string[]> = {
  mhu: ['Twa_mhu_deg', 'Twa_mhu_tm_deg', 'Twa_mhu_n_deg'],
  bow: ['Twa_bow_deg', 'Twa_bow_tm_deg', 'Twa_bow_n_deg'],
};

/**
 * TWD equivalents. Two deep rather than three: the processor only abs-normalises TWA/AWA/CWA, so
 * there is no `Twd_*_n_deg` variant to fall back to (and an abs wind direction would be meaningless).
 */
export const TWD_TACK_SENSOR_CHANNELS: Record<TwaTackSensor, readonly string[]> = {
  mhu: ['Twd_mhu_deg', 'Twd_mhu_tm_deg'],
  bow: ['Twd_bow_deg', 'Twd_bow_tm_deg'],
};

export const TWA_TACK_METRIC_CHANNELS: Record<
  TwaTackMetric,
  Record<TwaTackSensor, readonly string[]>
> = {
  twa: TWA_TACK_SENSOR_CHANNELS,
  twd: TWD_TACK_SENSOR_CHANNELS,
};

/**
 * Per-sensor TWS. Only km/h variants are streamed for the SGP vanes (no `Tws_bow_kts`), but the
 * scale is read off the channel name anyway — see {@link speedScaleToKph}.
 */
export const TWS_TACK_SENSOR_CHANNELS: Record<TwaTackSensor, readonly string[]> = {
  mhu: ['Tws_mhu_kph', 'Tws_mhu_tm_kph'],
  bow: ['Tws_bow_kph', 'Tws_bow_tm_kph'],
};

/**
 * Boat speed — a hull channel, not a per-sensor one, so the same list serves both sensor views.
 * GP50 streams kph; kts is the form other classes use.
 */
export const BSP_TACK_CHANNELS: readonly string[] = ['Bsp_kph', 'Bsp_kts'];

/** Column/caption text for each metric. */
export const TWA_TACK_METRIC_LABELS: Record<TwaTackMetric, string> = {
  twa: 'TWA',
  twd: 'TWD',
};

export const TWA_TACK_SENSOR_LABELS: Record<TwaTackSensor, string> = {
  mhu: 'MHU',
  bow: 'BOW',
};

/**
 * The views the overlay cycles through on double-click, in order. The first two compare both sensors
 * on one metric; the last two show all three measures for one sensor.
 */
export type TwaTackView = TwaTackMetric | TwaTackSensor;

export const TWA_TACK_VIEW_CYCLE: readonly TwaTackView[] = ['twa', 'twd', 'bow', 'mhu'];

/** True when the view shows one sensor's TWS/BSP/TWA rather than comparing both sensors. */
export function isTwaTackSensorView(view: TwaTackView): view is TwaTackSensor {
  return view === 'bow' || view === 'mhu';
}

/** Next view in {@link TWA_TACK_VIEW_CYCLE}, wrapping back to the first. */
export function nextTwaTackView(view: TwaTackView): TwaTackView {
  const i = TWA_TACK_VIEW_CYCLE.indexOf(view);
  return TWA_TACK_VIEW_CYCLE[(i + 1) % TWA_TACK_VIEW_CYCLE.length];
}

/** Short name for a view: the metric for the comparison views, the sensor for the readout views. */
export function twaTackViewLabel(view: TwaTackView): string {
  return isTwaTackSensorView(view)
    ? TWA_TACK_SENSOR_LABELS[view]
    : TWA_TACK_METRIC_LABELS[view];
}

/**
 * Channels requested from the streaming service. Every view is listed so a single fetch serves all
 * four — the merged endpoint returns every channel it has regardless of this list, which only
 * matters for the per-channel fallback path.
 */
export const TWA_TACK_CHANNELS = [
  'Twa_deg',
  ...TWA_TACK_SENSOR_CHANNELS.mhu,
  ...TWA_TACK_SENSOR_CHANNELS.bow,
  ...TWD_TACK_SENSOR_CHANNELS.mhu,
  ...TWD_TACK_SENSOR_CHANNELS.bow,
  ...TWS_TACK_SENSOR_CHANNELS.mhu,
  ...TWS_TACK_SENSOR_CHANNELS.bow,
  ...BSP_TACK_CHANNELS,
  'GRADE',
  'TACK',
];

/** Rolling window (ms) the overlay averages over. */
export const TWA_TACK_WINDOW_MS = 5 * 60 * 1000;

/** Minimum samples in a PORT/STBD cell before a mean is trusted enough to show. */
export const TWA_TACK_MIN_SAMPLES = 10;

/**
 * Only grade-3 samples are averaged, matching the Live Table focus panel (`pointMatchesGrade3`).
 *
 * The processor emits 3 or 1 and nothing else — 1 means within 10s of a maneuver, turning
 * (|Yaw_rate_dps| > 10), high-TWS/low-BSP, or under 60% of BSP/VMG target (processor.js GRADE block).
 * So a boat graded 1 for underperforming contributes nothing here, by design: the bar is steady,
 * on-target, straight-line sailing.
 */
export const TWA_TACK_REQUIRED_GRADE = 3;

export interface TwaTackRow {
  sensor: TwaTackSensor;
  label: string;
  port: number | null;
  stbd: number | null;
  /** STBD - PORT, only when both means are present. */
  delta: number | null;
  portCount: number;
  stbdCount: number;
  /** Which candidate channel actually supplied values, for diagnostics. */
  channel: string | null;
}

export interface TwaTackModeStats {
  mode: FocusPosMode;
  /** Which quantity `rows` holds. */
  metric: TwaTackMetric;
  rows: TwaTackRow[];
  /** Samples that passed the band + grade filters (before per-sensor availability). */
  sampleCount: number;
  /** Rows inspected before any filtering. */
  totalCount: number;
  /** Carried a sensor reading in the band but was not grade 3. */
  rejectedByGrade: number;
}

/** The three quantities a single-sensor view shows, in display order. */
export type TwaTackMeasure = 'tws' | 'bsp' | 'twa';

export const TWA_TACK_MEASURES: readonly TwaTackMeasure[] = ['tws', 'bsp', 'twa'];

export interface TwaTackMeasureRow {
  measure: TwaTackMeasure;
  /** First-column text, unit included (e.g. `TWS [kts]`). */
  label: string;
  /** Already converted to the requested display unit; speeds only, angles pass through. */
  port: number | null;
  stbd: number | null;
  /** STBD - PORT. All three measures are linear, so this is a plain difference. */
  delta: number | null;
  portCount: number;
  stbdCount: number;
  channel: string | null;
}

export interface TwaTackSensorModeStats {
  mode: FocusPosMode;
  sensor: TwaTackSensor;
  rows: TwaTackMeasureRow[];
  sampleCount: number;
  totalCount: number;
  rejectedByGrade: number;
}

/**
 * Diagnostic: channel keys for the given metric seen across the window. Unions over sampled rows
 * rather than reading row 0 — channels arriving at different cadences are absent from any single row.
 * A sensor view passes `'sensor'` to widen the pattern across all three of its measures.
 */
export function listTwaChannels(
  points: readonly Record<string, unknown>[],
  metric: TwaTackMetric | 'sensor' = 'twa'
): string[] {
  const pattern = metric === 'twd' ? /twd/i : metric === 'sensor' ? /tws|bsp|twa/i : /twa/i;
  const stride = Math.max(1, Math.ceil(points.length / 200));
  const keys = new Set<string>();
  for (let i = 0; i < points.length; i += stride) {
    for (const k of Object.keys(points[i])) {
      if (pattern.test(k)) keys.add(k);
    }
  }
  return [...keys].sort();
}

/**
 * Diagnostic: how many rows actually carry each channel the computation depends on. Pinpoints which
 * stage drops the data — a zero on `Twa_bow_deg` means the channel never arrives, whereas healthy
 * sensor counts alongside a zero `sampleCount` means the band / grade / carry gates are rejecting.
 */
export function summariseRowCoverage(
  points: readonly Record<string, unknown>[]
): Record<string, number> {
  const tracked = [
    'Twa_deg',
    'Twa_n_deg',
    'GRADE',
    'TACK',
    ...TWA_TACK_SENSOR_CHANNELS.mhu,
    ...TWA_TACK_SENSOR_CHANNELS.bow,
    ...TWD_TACK_SENSOR_CHANNELS.mhu,
    ...TWD_TACK_SENSOR_CHANNELS.bow,
    ...TWS_TACK_SENSOR_CHANNELS.mhu,
    ...TWS_TACK_SENSOR_CHANNELS.bow,
    ...BSP_TACK_CHANNELS,
  ];
  const counts: Record<string, number> = {};
  for (const channel of tracked) counts[channel] = 0;
  for (const pt of points) {
    for (const channel of tracked) {
      const raw = pt[channel];
      if (raw !== null && raw !== undefined) counts[channel] += 1;
    }
  }
  return counts;
}

/**
 * First candidate channel that yields a finite number on this sample. Speed series are normalised to
 * km/h from the unit implied by the channel name, so a `Bsp_kts` deployment averages the same numbers
 * as a `Bsp_kph` one.
 */
function readSensorValue(
  pt: Record<string, unknown>,
  candidates: readonly string[],
  speed = false
): { value: number; channel: string } | null {
  for (const channel of candidates) {
    const value = readNumericFromPoint(pt, channel);
    if (value !== null) {
      return { value: speed ? value * speedScaleToKph(channel) : value, channel };
    }
  }
  return null;
}

/** Boat tack for a sample: the processor `TACK` string when present, else the sign of canonical TWA. */
export function readPointTack(pt: Record<string, unknown>): 'port' | 'stbd' | null {
  const raw = pt['TACK'] ?? pt['tack'];
  if (typeof raw === 'string') {
    const t = raw.trim().toLowerCase();
    if (t === 'port' || t === 'p') return 'port';
    if (t === 'stbd' || t === 'starboard' || t === 's') return 'stbd';
  }
  const twa = readNumericFromPoint(pt, 'Twa_deg');
  if (twa === null || twa === 0) return null;
  return twa < 0 ? 'port' : 'stbd';
}

/**
 * How a series averages.
 * - `abs` — mean of |value|, so both tacks are directly comparable (|TWA|).
 * - `linear` — plain mean, for quantities that are already one-signed (TWS, BSP).
 * - `circular` — unit-vector mean, for compass bearings (TWD).
 */
type TackSpace = 'abs' | 'linear' | 'circular';

/**
 * One row's worth of instruction for {@link accumulateByTack}: which channels can supply it, and how
 * its samples combine.
 */
interface TackSeriesSpec {
  /** Row identity, used to look the result back up. */
  key: string;
  candidates: readonly string[];
  space: TackSpace;
  /** Speed channel: values are normalised to km/h on read, via {@link speedScaleToKph}. */
  speed?: boolean;
}

/**
 * One tack's running total. `sum` is the linear accumulator used for `abs`/`linear`; `sin`/`cos` are
 * the unit vector components used for the `circular` mean. Only the pair the space needs is fed.
 */
interface TackAccumulator {
  sum: number;
  sin: number;
  cos: number;
  n: number;
}

interface SeriesAccumulator {
  port: TackAccumulator;
  stbd: TackAccumulator;
  channel: string | null;
}

function emptyTack(): TackAccumulator {
  return { sum: 0, sin: 0, cos: 0, n: 0 };
}

function emptyAccumulator(): SeriesAccumulator {
  return { port: emptyTack(), stbd: emptyTack(), channel: null };
}

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

/** Feed one sample into a tack bucket, in whichever space the series averages in. */
function addSample(bucket: TackAccumulator, value: number, space: TackSpace): void {
  if (space === 'circular') {
    const rad = normalizeAngle(value) * DEG_TO_RAD;
    bucket.sin += Math.sin(rad);
    bucket.cos += Math.cos(rad);
  } else {
    bucket.sum += space === 'abs' ? Math.abs(value) : value;
  }
  bucket.n += 1;
}

/**
 * Bucket mean, or null when there are too few samples to trust it. `circular` uses the vector mean; a
 * near-zero resultant means the samples cancel out (spread over the whole compass), where any single
 * angle would be arbitrary — report nothing rather than a number the crew would act on.
 */
function meanOrNull(bucket: TackAccumulator, space: TackSpace): number | null {
  if (bucket.n < TWA_TACK_MIN_SAMPLES) return null;
  if (space !== 'circular') return bucket.sum / bucket.n;
  if (Math.hypot(bucket.sin, bucket.cos) < 1e-6) return null;
  return normalizeAngle(Math.atan2(bucket.sin, bucket.cos) * RAD_TO_DEG);
}

/** STBD - PORT. Wrapped into -180..180 for bearings, where the pair can straddle north. */
function deltaOrNull(port: number | null, stbd: number | null, space: TackSpace): number | null {
  if (port === null || stbd === null) return null;
  return space === 'circular' ? normalizeAngleDiff(stbd - port) : stbd - port;
}

/** How long a carried-forward context value stays valid (ms). */
export const TWA_TACK_CARRY_MS = 5000;

function readRowTimeMs(pt: Record<string, unknown>): number {
  const ts = pt['timestamp'] ?? pt['Datetime'] ?? pt['datetime'];
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return Number.isFinite(ts) ? ts : NaN;
  if (typeof ts === 'string') {
    const ms = new Date(ts).getTime();
    return Number.isFinite(ms) ? ms : NaN;
  }
  return NaN;
}

/** A value plus the row time it came from, so staleness can be bounded. */
interface Carried<T> {
  value: T | null;
  ts: number;
}

function fresh<T>(c: Carried<T>, nowMs: number): T | null {
  if (c.value === null) return null;
  if (!Number.isFinite(nowMs) || !Number.isFinite(c.ts)) return c.value;
  return nowMs - c.ts <= TWA_TACK_CARRY_MS ? c.value : null;
}

function tackFromString(raw: unknown): 'port' | 'stbd' | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim().toLowerCase();
  if (t === 'port' || t === 'p') return 'port';
  if (t === 'stbd' || t === 'starboard' || t === 's') return 'stbd';
  return null;
}

function inBand(absTwa: number, mode: FocusPosMode): boolean {
  switch (mode) {
    case 'upwind':
      return absTwa > 25 && absTwa < 75;
    case 'reaching':
      return absTwa >= 80 && absTwa <= 110;
    case 'downwind':
      return absTwa > 125 && absTwa < 160;
    default:
      return true;
  }
}

/** What {@link accumulateByTack} produces: one filled accumulator per spec, plus the gate tallies. */
interface TackAccumulation {
  /** Keyed by {@link TackSeriesSpec.key}, in the order the specs were given. */
  series: Map<string, SeriesAccumulator>;
  /** Samples that passed the band + grade filters (before per-series availability). */
  sampleCount: number;
  /** Carried a series reading in the band but was not grade 3. */
  rejectedByGrade: number;
}

/**
 * Per-tack accumulation shared by every view. Restricted to the given point-of-sail band (upwind
 * 25–75°, downwind 125–160°) and to grade-3 samples ({@link TWA_TACK_REQUIRED_GRADE}). The band and
 * tack always come from canonical `Twa_deg` / `TACK`, so switching view re-averages exactly the same
 * samples.
 *
 * Merged rows are pivoted per Influx `_time`, so a row carrying `Twa_bow_deg` frequently has no
 * `Twa_deg` or `GRADE` at all — requiring all three on one row throws away nearly everything.
 * Band / grade / tack are therefore carried forward from the most recent row that had them (up to
 * {@link TWA_TACK_CARRY_MS}), while each series value is counted only on the row it actually
 * appears on, so a slow sensor is not repeat-counted at the row rate.
 *
 * `points` must be ascending by time — the merged endpoint and `fetchMergedData` both sort.
 */
function accumulateByTack(
  points: readonly Record<string, unknown>[],
  mode: FocusPosMode,
  specs: readonly TackSeriesSpec[]
): TackAccumulation {
  const series = new Map<string, SeriesAccumulator>(
    specs.map((spec) => [spec.key, emptyAccumulator()])
  );
  let sampleCount = 0;
  let rejectedByGrade = 0;

  // A window with no GRADE at all must not blank the panel — only gate on grade when it exists.
  const hasAnyGrade = points.some((pt) => pt['GRADE'] !== null && pt['GRADE'] !== undefined);

  const absTwa: Carried<number> = { value: null, ts: NaN };
  const signedTwa: Carried<number> = { value: null, ts: NaN };
  const grade: Carried<number> = { value: null, ts: NaN };
  const tackStr: Carried<'port' | 'stbd'> = { value: null, ts: NaN };

  for (const pt of points) {
    const rowMs = readRowTimeMs(pt);

    const signed = readNumericFromPoint(pt, 'Twa_deg');
    if (signed !== null) {
      signedTwa.value = signed;
      signedTwa.ts = rowMs;
      absTwa.value = Math.abs(signed);
      absTwa.ts = rowMs;
    } else {
      const normalised = readNumericFromPoint(pt, 'Twa_n_deg');
      if (normalised !== null) {
        absTwa.value = Math.abs(normalised);
        absTwa.ts = rowMs;
      }
    }

    const g = readNumericFromPoint(pt, 'GRADE');
    if (g !== null) {
      grade.value = g;
      grade.ts = rowMs;
    }

    const t = tackFromString(pt['TACK'] ?? pt['tack']);
    if (t !== null) {
      tackStr.value = t;
      tackStr.ts = rowMs;
    }

    // Only rows that actually carry a series reading contribute.
    const hits = specs
      .map((spec) => ({ spec, hit: readSensorValue(pt, spec.candidates, spec.speed) }))
      .filter((h) => h.hit !== null);
    if (hits.length === 0) continue;

    const band = fresh(absTwa, rowMs);
    if (band === null || !inBand(band, mode)) continue;

    if (hasAnyGrade) {
      const gradeNow = fresh(grade, rowMs);
      if (gradeNow === null || Math.round(gradeNow) !== TWA_TACK_REQUIRED_GRADE) {
        rejectedByGrade += 1;
        continue;
      }
    }

    let tack = fresh(tackStr, rowMs);
    if (tack === null) {
      const signedNow = fresh(signedTwa, rowMs);
      if (signedNow === null || signedNow === 0) continue;
      tack = signedNow < 0 ? 'port' : 'stbd';
    }
    sampleCount += 1;

    for (const { spec, hit } of hits) {
      const bucket = series.get(spec.key)!;
      bucket.channel = bucket.channel ?? hit!.channel;
      addSample(tack === 'port' ? bucket.port : bucket.stbd, hit!.value, spec.space);
    }
  }

  return { series, sampleCount, rejectedByGrade };
}

/**
 * Mean |TWA| (or mean TWD — see `metric`) per sensor per tack over `points`. Filtering, carry-forward
 * and per-tack bucketing are all {@link accumulateByTack}'s; this only names the two series and
 * shapes the rows.
 */
export function computeTwaTackStats(
  points: readonly Record<string, unknown>[],
  mode: FocusPosMode,
  metric: TwaTackMetric = 'twa'
): TwaTackModeStats {
  const sensorChannels = TWA_TACK_METRIC_CHANNELS[metric];
  const space: TackSpace = metric === 'twd' ? 'circular' : 'abs';
  const acc = accumulateByTack(
    points,
    mode,
    TWA_TACK_SENSORS.map((sensor) => ({
      key: sensor,
      candidates: sensorChannels[sensor],
      space,
    }))
  );

  const rows = TWA_TACK_SENSORS.map<TwaTackRow>((sensor) => {
    const bucket = acc.series.get(sensor)!;
    const port = meanOrNull(bucket.port, space);
    const stbd = meanOrNull(bucket.stbd, space);
    return {
      sensor,
      label: TWA_TACK_SENSOR_LABELS[sensor],
      port,
      stbd,
      delta: deltaOrNull(port, stbd, space),
      portCount: bucket.port.n,
      stbdCount: bucket.stbd.n,
      channel: bucket.channel,
    };
  });

  return {
    mode,
    metric,
    rows,
    sampleCount: acc.sampleCount,
    totalCount: points.length,
    rejectedByGrade: acc.rejectedByGrade,
  };
}

/** Candidate channels for one measure of one sensor. BSP is a hull channel, so it ignores `sensor`. */
function measureChannels(measure: TwaTackMeasure, sensor: TwaTackSensor): readonly string[] {
  switch (measure) {
    case 'tws':
      return TWS_TACK_SENSOR_CHANNELS[sensor];
    case 'bsp':
      return BSP_TACK_CHANNELS;
    default:
      return TWA_TACK_SENSOR_CHANNELS[sensor];
  }
}

/**
 * First-column text. Deliberately bare — the panel is a glanceable map overlay, and the crew already
 * knows the boat's units. The active unit lives on the hover title instead.
 */
export function twaTackMeasureLabel(measure: TwaTackMeasure): string {
  return measure.toUpperCase();
}

/**
 * Speed unit a project displays in, inferred from its default speed channel (`Bsp_kph` → kph).
 * The project's channel naming is the convention every other chart, gauge and map overlay follows,
 * so the overlay reads the same unit as the panel next to it rather than a user-level preference.
 */
export function twaTackUnitFromChannel(channelName: string | undefined): SpeedDisplayUnit {
  return speedScaleToKph(channelName) === 1 ? SPEED_UNIT_KPH : SPEED_UNIT_KTS;
}

/**
 * TWS, BSP and mean |TWA| for one sensor, per tack, over the same window and the same filters as
 * {@link computeTwaTackStats} — so double-clicking between views never changes which samples count.
 *
 * All three are linear quantities (|TWA| is abs'd, TWS/BSP are one-signed), so DELTA is a plain
 * STBD - PORT throughout. Speeds are accumulated in km/h and converted once here, which keeps a
 * `Bsp_kts` deployment averaging the same numbers as a `Bsp_kph` one.
 */
export function computeTwaTackSensorStats(
  points: readonly Record<string, unknown>[],
  mode: FocusPosMode,
  sensor: TwaTackSensor,
  displayUnit: SpeedDisplayUnit
): TwaTackSensorModeStats {
  const specs = TWA_TACK_MEASURES.map<TackSeriesSpec>((measure) => ({
    key: measure,
    candidates: measureChannels(measure, sensor),
    space: measure === 'twa' ? 'abs' : 'linear',
    speed: measure !== 'twa',
  }));
  const acc = accumulateByTack(points, mode, specs);

  const rows = specs.map<TwaTackMeasureRow>((spec) => {
    const measure = spec.key as TwaTackMeasure;
    const bucket = acc.series.get(spec.key)!;
    // Angles are already in degrees; only the speed series needs the km/h → display conversion.
    const toDisplay = (v: number | null) =>
      v === null || !spec.speed ? v : convertSpeedMagnitude(v, SPEED_UNIT_KPH, displayUnit);

    const port = toDisplay(meanOrNull(bucket.port, spec.space));
    const stbd = toDisplay(meanOrNull(bucket.stbd, spec.space));
    return {
      measure,
      label: twaTackMeasureLabel(measure),
      port,
      stbd,
      delta: deltaOrNull(port, stbd, spec.space),
      portCount: bucket.port.n,
      stbdCount: bucket.stbd.n,
      channel: bucket.channel,
    };
  });

  return {
    mode,
    sensor,
    rows,
    sampleCount: acc.sampleCount,
    totalCount: points.length,
    rejectedByGrade: acc.rejectedByGrade,
  };
}

/** True when at least one cell in the upwind/downwind stats has a value worth rendering. */
export function twaTackStatsHaveData(
  stats: readonly { rows: readonly { port: number | null; stbd: number | null }[] }[]
): boolean {
  return stats.some((s) => s.rows.some((r) => r.port !== null || r.stbd !== null));
}

/**
 * Whole degrees, matching how the crew reads the instruments. TWD wraps after rounding so a mean of
 * 359.6 reads 0 rather than a compass bearing of 360.
 */
export function formatTwaTackValue(value: number | null, metric: TwaTackMetric = 'twa'): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value);
  return String(metric === 'twd' ? rounded % 360 : rounded);
}

/** Signed whole degrees for the DELTA column. */
export function formatTwaTackDelta(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return String(Math.round(value));
}

/**
 * Sensor-view cells. Speeds get one decimal (matching the live BSP table), angles whole degrees —
 * a 0.4 kt tack-to-tack difference is worth seeing, a 0.4° one is not.
 */
export function formatTwaTackMeasureValue(
  value: number | null,
  measure: TwaTackMeasure
): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return measure === 'twa' ? String(Math.round(value)) : value.toFixed(1);
}
